/**
 * State of the video player. Can also be accessed through the global YT object
 * @see https://developers.google.com/youtube/iframe_api_reference#onStateChange
 */
export enum PlayerState {
  UNSTARTED = -1,
  ENDED = 0,
  PLAYING = 1,
  PAUSED = 2,
  BUFFERING = 3,
  CUED = 5,
}

/**
 * Possible values for the error event that is fired when an error occurs in the player
 * @see https://developers.google.com/youtube/iframe_api_reference#onError
 */
export enum PlayerError {
  InvalidParam = 2,
  Html5Error = 5,
  VideoNotFound = 100,
  EmbeddingNotAllowed = 101,
  EmbeddingNotAllowed2 = 150,
}

/**
 * Possible values for the error event that is fired when the playback quality is changed
 * @see https://developers.google.com/youtube/iframe_api_reference#onPlaybackQualityChange
 */
export type VideoQuality =
  | 'small'
  | 'medium'
  | 'large'
  | 'hd720'
  | 'hd1080'
  | 'highres';

/**
 * Base type for events triggered by a player.
 */
type PlayerEvent = {
  /**
   * Video player that fired the event.
   */
  target: any;
};

/**
 * Base type for player events handlers
 */
type PlayerEventHandler<T extends PlayerEvent> = (event: T) => void;

/**
 * Event fired when the player's state changes
 * @see https://developers.google.com/youtube/iframe_api_reference#onStateChange
 */
export type OnStateChangeEvent = PlayerEvent & {
  data: PlayerState;
};

/**
 * Event fired when the player's playback quality changes
 * @see https://developers.google.com/youtube/iframe_api_reference#onPlaybackQualityChange
 */
export type OnPlaybackQualityChangeEvent = PlayerEvent & {
  data: VideoQuality;
};

/**
 * Event fired when the player's playback rate changes
 * @see https://developers.google.com/youtube/iframe_api_reference#onPlaybackRateChange
 */
export type OnPlaybackRateChangeEvent = PlayerEvent & {
  data: number;
};

/**
 * Event fired when an error occurs in the player
 * @see https://developers.google.com/youtube/iframe_api_reference#onError
 */
export type OnErrorEvent = PlayerEvent & {
  data: PlayerError;
};

/**
 * Event fired when the has loaded or unloaded a module with exposed API methods
 * @see https://developers.google.com/youtube/iframe_api_reference#onApiChange
 */
export type onApiChange = PlayerEvent & {
  data: PlayerError;
};

/**
 * @see https://developers.google.com/youtube/iframe_api_reference#Events
 */
export type PlayerEvents = {
  /**
   * Fires whenever a player has finished loading and is ready to begin receiving API calls.
   */
  onReady?: PlayerEventHandler<PlayerEvent>;

  /**
   * Fires whenever the player's state changes.
   * The data property of the event object that the API passes to your event listener function specifies an
   * integer that corresponds to the new player state
   */
  onStateChange?: PlayerEventHandler<OnStateChangeEvent>;

  /**
   * Fires whenever the video playback quality changes
   */
  onPlaybackQualityChange?: PlayerEventHandler<OnPlaybackQualityChangeEvent>;

  /**
   * Fires whenever the video playback rate changes
   */
  onPlaybackRateChange?: PlayerEventHandler<OnPlaybackRateChangeEvent>;

  /**
   * Fires if an error occurs in the player. The API will pass an event object to the event listener function.
   * That object's data property will specify an integer that identifies the type of error that occurred
   */
  onError?: PlayerEventHandler<OnErrorEvent>;

  /**
   * Fires to indicate that the player has loaded (or unloaded) a module with exposed API methods
   */
  onApiChange?: PlayerEventHandler<PlayerEvent>;
};
